import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { brl, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, Loader2, ShieldCheck, PenLine, FileDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import logoDark from "@/assets/logo-dark.png";
import { FREQ } from "@/lib/status";

export default function PublicSignContract() {
  const { token } = useParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const [nome, setNome] = useState("");
  const [aceite, setAceite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/contracts/${token}/pdf`;

  useEffect(() => {
    api.get(`/public/contracts/${token}`)
      .then(({ data }) => { setC(data); setNome(data.cliente_nome || ""); })
      .catch((e) => {
        const st = e.response?.status;
        if (st === 410) setError(e.response.data?.detail);
        else if (st === 404) setError("Link inválido ou contrato não localizado. Peça à empresa um novo link de acesso ao contrato.");
        else setError("Não foi possível carregar o contrato agora. Verifique sua conexão e tente novamente.");
      });
  }, [token]);

  const sign = async () => {
    if (!aceite) { toast.error("Você precisa aceitar os termos."); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/public/contracts/${token}/sign`, { nome_confirmado: nome, aceite });
      setDone(data);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao assinar"); }
    finally { setLoading(false); }
  };

  if (error)
    return <Center><AlertTriangle className="w-12 h-12 text-red-400 mb-4" /><p className="text-slate-300 text-center max-w-sm" data-testid="sign-error">{error}</p></Center>;
  if (!c) return <Center><Loader2 className="w-8 h-8 animate-spin gold-text" /></Center>;


  if (done || (c.status !== "contrato_pendente"))
    return (
      <Center>
        <div className="w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-emerald-400" />
        </div>
        <h1 className="text-white text-2xl font-bold" data-testid="sign-success-title">Contrato assinado!</h1>
        <p className="text-slate-400 mt-2 text-center max-w-sm">
          O contrato {c.numero} foi assinado digitalmente{done?.ip ? ` (IP ${done.ip})` : ""}. Guarde uma via em PDF.
        </p>
        <a href={pdfUrl} target="_blank" rel="noreferrer" className="mt-6">
          <Button className="bg-gold hover:bg-[hsl(var(--gold-hover))] text-black gap-2" data-testid="sign-download-pdf"><FileDown className="w-4 h-4" /> Baixar contrato (PDF)</Button>
        </a>
      </Center>
    );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-sign-page">
      <header className="bg-onyx py-3 flex justify-center sticky top-0 z-10"><img src={logoDark} alt="TRT" className="h-12" /></header>
      <div className="max-w-2xl mx-auto p-4 md:p-8 space-y-5">
        <div className="text-center">
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">Assinatura do Contrato</h1>
          <p className="text-slate-500 mt-1">Contrato nº {c.numero}</p>
        </div>

        <div className="trt-card p-6 space-y-3 text-sm">
          <Row l="Contratante (você)" v={c.cliente_nome} />
          <Row l="CPF" v={c.cpf} />
          <Row l="Credor" v={c.company?.razao_social} />
          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
            <Box l="Valor liberado" v={brl(c.valor_liberado)} />
            <Box l="Valor total" v={brl(c.valor_total)} gold />
            <Box l="Parcelas" v={`${c.num_parcelas}x de ${brl(c.valor_parcela)}`} />
            <Box l="Periodicidade" v={FREQ[c.frequencia] || c.frequencia} />
            <Box l="1º vencimento" v={fmtDate(c.primeiro_vencimento)} />
            <Box l="Último vencimento" v={fmtDate(c.ultimo_vencimento)} />
          </div>
          {c.garantias && <div className="text-slate-600 pt-2 border-t"><b>Garantias:</b> {c.garantias}</div>}
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 gold-text text-sm hover:underline pt-2" data-testid="sign-view-pdf">
            <FileDown className="w-4 h-4" /> Ler contrato completo (PDF)
          </a>
        </div>

        <div className="trt-card p-6 space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid="sign-observacao">
            <b>Observação:</b> A assinatura do contrato somente será necessária quando solicitada pela empresa. Até que seja solicitada, nenhuma assinatura é necessária.
          </div>
          <div>
            <Label className="text-sm">Confirme seu nome completo</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} className="mt-1.5" data-testid="sign-nome-input" />
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={aceite} onCheckedChange={setAceite} data-testid="sign-aceite" className="mt-0.5" />
            <span className="text-sm text-slate-600">Li e concordo com todas as cláusulas e condições do contrato acima, reconhecendo a dívida e assumindo a obrigação de pagamento conforme descrito.</span>
          </label>
          <Button onClick={sign} disabled={loading || !aceite} className="w-full bg-gold hover:bg-[hsl(var(--gold-hover))] text-black h-12 font-bold gap-2" data-testid="sign-confirm-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><PenLine className="w-5 h-5" /> Assinar contrato</>}
          </Button>
          <div className="flex items-center justify-center gap-2 text-slate-400 text-xs">
            <ShieldCheck className="w-4 h-4" /> Sua assinatura será registrada com data, hora e IP.
          </div>
        </div>
      </div>
    </div>
  );
}

const Center = ({ children }) => (
  <div className="min-h-screen bg-onyx flex flex-col items-center justify-center p-6">{children}</div>
);
const Row = ({ l, v }) => (<div className="flex justify-between"><span className="text-slate-500">{l}</span><span className="font-medium">{v || "-"}</span></div>);
const Box = ({ l, v, gold }) => (<div><div className="text-xs text-slate-400">{l}</div><div className={`font-bold mono ${gold ? "gold-text" : ""}`}>{v}</div></div>);
